import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import settings from '@nobia/zeus-components/lib/settings'
import { withTranslation } from '@nobia/zeus-components/lib/i18n'
import Button from '@nobia/zeus-components/lib/buttons'
import styled, { theme, rem } from '@nobia/zeus-components/lib/styled'
import { Row, Column } from '@nobia/zeus-components/lib/grid'
import ErrorBoundary from '@nobia/zeus-components/lib/errorBoundary'
import ProductCard from '../product-card'
import GridCarousel from '../image-carousel/GridCarousel'

export const ProductRow = styled(Row)`
  display: grid;
  grid-auto-flow: row dense;
  grid-template-columns: calc(100% / 3) calc(100% / 3) calc(100% / 3);
  margin-left: -${theme('productGrid.marginLeft')};
  margin-right: -${theme('productGrid.marginRight')};
  @media (max-width: 1280px) {
    grid-template-columns: calc(100% / 3) calc(100% / 3) calc(100% / 3);
  }
  @media (max-width: 960px) {
    grid-template-columns: 50% 50%;
  }
  @media (max-width: 720px) {
    grid-template-columns: 100%;
  }
`

export const ProductColumn = styled(Column)`
  display: flex;
  margin: ${rem(10)} 0;
  padding: 0 16px;
  width: 100%;
  max-width: 100%;
  @media (max-width: 450px) {
    margin: ${rem(12)} 0;
  }
`
export const StyledHeadline = styled.h3`
  font-family: ${theme('styledHeadline.fontFamily')};
  font-size: ${theme('styledHeadline.fontSize')};
  font-weight: ${theme('styledHeadline.fontWeight')};
  @media (max-width: 550px) {
    font-size: ${theme('styledHeadline.mobile.fontSize')};
  }
`
const StyledButton = styled(Button)`
  display: flex;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: ${rem(15)};
  margin-top: ${rem(15)};
  border-radius: 3px;
  font-weight: 700;
`

const StyledFilterLabel = withTranslation('app.productgrid')(styled('span')`
  min-width: 97px;
`)

const ProductGrid = ({
  products,
  title,
  showBadge,
  skipLastSlug,
  linkToProductPage,
  showVariants,
  className,
  name,
}) => {
  const defaultGridSize = 3
  const expandedGridSize = 6

  const desktopSize = 960
  const [showMoreButton, setShowMoreButton] = useState(
    (products.length > 3 && settings.brand === 'marbodal') ||
      (settings.brand === 'marbodal' && window.innerWidth > desktopSize) ||
      (products.length > 3 && settings.brand === 'novart') ||
      (settings.brand === 'novart' && window.innerWidth > desktopSize) ||
      (products.length > 3 && settings.brand === 'hthdk') ||
      (settings.brand === 'hthdk' && window.innerWidth > desktopSize)
  )
  const [showMoreToggle, setShowMoreToggle] = useState(true)
  const [gridSize, setGridSize] = useState(defaultGridSize)

  const toggleShowMore = () => {
    const toggleValue = !showMoreToggle
    setShowMoreToggle(toggleValue)
    setGridSize(toggleValue ? defaultGridSize : expandedGridSize)
  }

  function handleResize() {
    setShowMoreButton(
      (products.length > 3 && settings.brand === 'marbodal') ||
        (settings.brand === 'marbodal' && window.innerWidth > desktopSize) ||
        (products.length > 3 && settings.brand === 'novart') ||
        (settings.brand === 'novart' && window.innerWidth > desktopSize) ||
        (products.length > 3 && settings.brand === 'hthdk') ||
        (settings.brand === 'hthdk' && window.innerWidth > desktopSize) ||
        (products.length > 3 && settings.brand === 'sigdal') ||
        (settings.brand === 'sigdal' && window.innerWidth > desktopSize)
    )
  }

  useEffect(() => {
    // only execute all the code below in client side
    if (typeof window !== 'undefined') {
      // Handler to call on window resize

      // Add event listener
      window.addEventListener('resize', handleResize)

      // Call handler right away so state gets updated with initial window size
      handleResize()

      // Remove event listener on cleanup
      return () => window.removeEventListener('resize', handleResize)
    }
    return ''
  }, [])

  return (
    <div className={className}>
      {title && products.length > 0 && (
        <Row>
          <Column>
            <StyledHeadline>{title}</StyledHeadline>
          </Column>
        </Row>
      )}

      <ErrorBoundary name={name}>
        <div
          style={{
            maxWidth: '100%',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {showMoreToggle ? (
            <GridCarousel show={gridSize} hideArrows={showMoreButton}>
              {products &&
                products.map(
                  (product) =>
                    product &&
                    product.filterPage && (
                      <ProductCard
                        key={product.id}
                        skipLastSlug={skipLastSlug}
                        product={product}
                        showBadge={showBadge}
                        showVariants={showVariants}
                        linkToProductPage={linkToProductPage}
                      />
                    )
                )}
            </GridCarousel>
          ) : (
            <ProductRow showMoreToggle={showMoreToggle}>
              {products &&
                products.map(
                  (product) =>
                    product &&
                    product.filterPage && (
                      <ProductColumn sm={3} md={3} lg={3} key={product.id}>
                        <ProductCard
                          key={product.id}
                          skipLastSlug={skipLastSlug}
                          product={product}
                          showBadge={showBadge}
                          showVariants={showVariants}
                          linkToProductPage={linkToProductPage}
                        />
                      </ProductColumn>
                    )
                )}
            </ProductRow>
          )}

          {showMoreButton && products.length > 3 && (
            <StyledButton
              kind="inverted"
              size="normal"
              onClick={toggleShowMore}
            >
              <StyledFilterLabel
                intlKey={showMoreToggle ? 'show_all' : 'show_less'}
              />
            </StyledButton>
          )}
        </div>
      </ErrorBoundary>
    </div>
  )
}

ProductGrid.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  title: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  showBadge: PropTypes.bool,
  showVariants: PropTypes.bool,
  skipLastSlug: PropTypes.bool,
  linkToProductPage: PropTypes.bool,
}

ProductGrid.defaultProps = {
  title: undefined,
  name: 'products',
  className: undefined,
  showBadge: true,
  showVariants: false,
  sm: 6,
  md: 3,
  lg: 2,
  skipLastSlug: false,
  linkToProductPage: false,
}

export default ProductGrid
